import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  FiMoreVertical,
  FiX,
  FiAlertTriangle,
  FiTrash2,
  FiXCircle,
} from "react-icons/fi";
import { subscriptionsAPI } from "../services/api";
import toast from "react-hot-toast";

const ManagementContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const MenuButton = styled.button`
  background: #f0f0f0;
  border: 1px solid #ddd;
  color: #666;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 32px;

  &:hover {
    background: #e0e0e0;
    color: #333;
    border-color: #bbb;
  }
`;

const MenuDropdown = styled(motion.div)`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 2px solid #2d5016;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  z-index: 9999;
  min-width: 200px;
  overflow: hidden;
  margin-top: 4px;
`;

const MenuItem = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #333;
  transition: background-color 0.2s ease;
  font-size: 14px;
  font-weight: 500;

  &:hover {
    background: #f8f9fa;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &.danger {
    color: #dc3545;

    &:hover {
      background: #f8d7da;
    }
  }

  &.warning {
    color: #fd7e14;

    &:hover {
      background: #fff3cd;
    }
  }
`;

const ConfirmationModal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 400px;
  width: 100%;
  text-align: center;
`;

const ModalTitle = styled.h3`
  color: #2d5016;
  margin-bottom: 1rem;
  font-size: 1.2rem;
  font-weight: 600;
`;

const ModalMessage = styled.p`
  color: #666;
  margin-bottom: 2rem;
  line-height: 1.5;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
`;

const ModalButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &.primary {
    background: #2d5016;
    color: white;

    &:hover {
      background: #1e3a0f;
    }
  }

  &.secondary {
    background: #6c757d;
    color: white;

    &:hover {
      background: #545b62;
    }
  }

  &.danger {
    background: #dc3545;
    color: white;

    &:hover {
      background: #c82333;
    }
  }
`;

const SubscriptionManagement = ({ subscription, onUpdate, onDelete }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [loading, setLoading] = useState(false);
  const menuRef = useRef(null);

  const startDate = new Date(subscription.start_date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const hasStarted = startDate <= today;

  // Debug logging
  console.log("Subscription Management Debug:", {
    subscriptionId: subscription.id,
    status: subscription.status,
    startDate: subscription.start_date,
    hasStarted,
    canDelete: !hasStarted && subscription.status !== "cancelled",
    canCancel: subscription.status === "active",
  });

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  const handleDelete = async () => {
    setLoading(true);
    try {
      await subscriptionsAPI.deleteSubscription(subscription.id);
      toast.success("Subscription deleted successfully");
      onDelete(subscription.id);
      setShowConfirmModal(false);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to delete subscription"
      );
    } finally {
      setLoading(false);
    }
  };

  const openConfirmModal = (type) => {
    setActionType(type);
    setShowConfirmModal(true);
    setShowMenu(false);
  };

  const getActionText = () => {
    // Only delete action is available - subscriptions are one-time purchases
    if (actionType === "delete") {
      return {
        title: "Delete Subscription",
        message:
          "Are you sure you want to delete this subscription? This action cannot be undone.",
        buttonText: "Delete Subscription",
        buttonClass: "danger",
      };
    }
    return null;
  };

  const actionText = getActionText();

  return (
    <>
      <ManagementContainer ref={menuRef}>
        <MenuButton
          onClick={() => {
            console.log("Menu button clicked, current showMenu:", showMenu);
            setShowMenu(!showMenu);
          }}
        >
          <FiMoreVertical />
        </MenuButton>

        {showMenu && (
          <MenuDropdown
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            onAnimationStart={() => console.log("Menu dropdown animating in")}
          >
            {/* Cancel option removed - subscriptions are one-time purchases paid upfront */}
            {!hasStarted && subscription.status !== "cancelled" && (
              <MenuItem
                className="danger"
                onClick={() => openConfirmModal("delete")}
              >
                <FiTrash2 />
                Delete Subscription
              </MenuItem>
            )}

            {subscription.status === "cancelled" && !hasStarted && (
              <MenuItem
                className="danger"
                onClick={() => openConfirmModal("delete")}
              >
                <FiTrash2 />
                Delete Cancelled Subscription
              </MenuItem>
            )}

            {subscription.status === "cancelled" && hasStarted && (
              <MenuItem
                disabled
                style={{ color: "#999", cursor: "not-allowed" }}
              >
                Subscription completed - no actions available
              </MenuItem>
            )}

            {subscription.status !== "active" &&
              subscription.status !== "cancelled" &&
              hasStarted && (
                <MenuItem
                  disabled
                  style={{ color: "#999", cursor: "not-allowed" }}
                >
                  No actions available
                </MenuItem>
              )}
          </MenuDropdown>
        )}
      </ManagementContainer>

      {showConfirmModal && actionText && (
        <ConfirmationModal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowConfirmModal(false)}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{actionText.title}</ModalTitle>
            <ModalMessage>{actionText.message}</ModalMessage>

            <ModalActions>
              <ModalButton
                className="secondary"
                onClick={() => setShowConfirmModal(false)}
                disabled={loading}
              >
                Cancel
              </ModalButton>
              <ModalButton
                className={actionText.buttonClass}
                onClick={handleDelete}
                disabled={loading}
              >
                {loading ? "Processing..." : actionText.buttonText}
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ConfirmationModal>
      )}
    </>
  );
};

export default SubscriptionManagement;
